import { useState } from 'react';
import type { GuaranteedServiceOrgSkuEntry, GuaranteedServiceSkuEntry, PatchGuaranteedServiceRequest } from '@nadoutong/contracts';
import { skuNamespaceKey } from '../../components/SkuGroupPicker.tsx';
import { useT } from '../../i18n/index.ts';
import { InfoHint, Panel } from '../../ui/index.ts';
import { OrgOverlayTable } from './OrgOverlayTable.tsx';
import { SKU_GRID, SkuConfigCard } from './SkuConfigCard.tsx';

function toggleSet(current: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(current);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

export function ConfigCards({
  skuRows,
  orgRows,
  customerName,
  customerEmail,
  onPatch,
  onEditNote,
  onDelete,
}: {
  readonly skuRows: readonly GuaranteedServiceSkuEntry[];
  readonly orgRows: readonly GuaranteedServiceOrgSkuEntry[];
  readonly customerName: (orgId: string) => string;
  readonly customerEmail: (orgId: string) => string;
  readonly onPatch: (id: string, body: PatchGuaranteedServiceRequest) => void;
  readonly onEditNote: (id: string, note: string) => void;
  readonly onDelete: (id: string) => void;
}): JSX.Element {
  const t = useT();
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const buckets = new Map<string, GuaranteedServiceSkuEntry[]>();
  for (const row of skuRows) {
    const key = skuNamespaceKey(row.sku_name);
    const existing = buckets.get(key);
    buckets.set(key, existing === undefined ? [row] : [...existing, row]);
  }
  const keys = [...buckets.keys()].sort((left, right) => {
    if (left === '') return 1;
    if (right === '') return -1;
    return left.localeCompare(right);
  });
  return (
    <Panel
      testId="guaranteed-config-card"
      caption={(
        <span className="inline-flex items-baseline gap-2">
          <span>{t('guaranteed.section.config')}</span>
          <span className="text-label font-normal text-paper-low">
            {t('guaranteed.config_count', { sku: String(skuRows.length), org: String(orgRows.length) })}
          </span>
        </span>
      )}
    >
      <div className="overflow-x-auto">
        <div className={`${SKU_GRID} min-w-[1080px] border-b border-line-soft px-3 py-2 text-label font-medium text-paper-low`}>
          <span>{t('guaranteed.col.sku')}</span>
          <span>{t('guaranteed.col.probe')}</span>
          <span>{t('guaranteed.col.chain')}</span>
          <span>{t('guaranteed.col.jev')}</span>
          <span className="inline-flex items-center justify-end gap-1">
            {t('guaranteed.col.stats')}
            <InfoHint
              label={t('guaranteed.stats_hint_label')}
              content={t('guaranteed.stats_hint')}
              testId="guaranteed-entered-hint"
            />
          </span>
          <span />
        </div>
        {skuRows.length === 0 ? (
          <p className="m-0 px-3 py-3 text-dense text-paper-low">{t('guaranteed.empty')}</p>
        ) : keys.map((key) => {
          const token = key === '' ? 'other' : key;
          const open = !closed.has(token);
          const label = key === '' ? t('guaranteed.sku_group_other') : key;
          return (
            <div key={token} data-testid={`guaranteed-config-namespace-${token}`}>
              <button
                type="button"
                className="flex w-full items-center bg-raised/40 px-3 py-1.5 text-left text-label font-medium text-paper-mid"
                aria-expanded={open}
                onClick={() => setClosed((current) => toggleSet(current, token))}
              >
                {open ? '▾' : '▸'} {label}
              </button>
              {open ? (buckets.get(key) ?? []).map((row) => (
                <SkuConfigCard
                  key={row.id}
                  row={row}
                  onPatch={(body) => onPatch(row.id, body)}
                  onEditNote={() => onEditNote(row.id, row.note ?? '')}
                  onDelete={() => onDelete(row.id)}
                />
              )) : null}
            </div>
          );
        })}
      </div>
      <div className="border-t-2 border-line">
        <div className="flex items-baseline gap-2 px-3 pt-2 text-label font-medium text-paper-mid">
          <span>{t('guaranteed.overlay.title')}</span>
          <span className="font-normal text-paper-low">{t('guaranteed.overlay.hint')}</span>
        </div>
        {orgRows.length === 0 ? (
          <p className="m-0 px-3 py-3 text-dense text-paper-low" data-testid="guaranteed-overlay-empty">
            {t('guaranteed.overlay.empty')}
          </p>
        ) : (
          <OrgOverlayTable
            rows={orgRows}
            customerName={customerName}
            customerEmail={customerEmail}
            onPatch={onPatch}
            onEditNote={onEditNote}
            onDelete={onDelete}
          />
        )}
      </div>
    </Panel>
  );
}
